import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';

const DocumentaryCard = ({ item }) => {
  const navigate = useNavigate();

  return (
    <div 
      onClick={() => navigate(`/content/${item.id}`)}
      className="group flex-shrink-0 w-[180px] md:w-[220px] cursor-pointer"
    >
      {/* Card */}
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-[#1a1a1a]">
        {/* Image */}
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        
        {/* Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
        
        {/* Lock icon (top right) */}
        <div className="absolute top-2 right-2">
          <div className="w-6 h-6 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
            <svg className="w-3 h-3 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>
        
        {/* Content at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="text-white text-sm font-semibold mb-1 line-clamp-2 group-hover:text-[#e4002b] transition-colors">
            {item.title}
          </h3>
          <p className="text-white/50 text-xs">
            {item.composer || item.director}
          </p>
          
          {/* Video duration indicator */}
          {item.duration && (
            <div className="flex items-center gap-1 mt-2 text-white/60 text-xs">
              <Play className="w-3 h-3" />
              <span>VIDEO · {item.duration}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DocumentaryCarousel = ({ title, items = [] }) => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (items.length === 0) return null;

  return (
    <section className="py-8 md:py-12">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl md:text-2xl font-semibold italic">
            {title}
          </h2>
          
          {/* Navigation */}
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {items.map((item) => (
            <DocumentaryCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DocumentaryCarousel;
